import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowLeft, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import Logo from "../../components/shared/Logo";
import { authAPI } from "../../lib/api";

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await authAPI.requestPasswordReset(email);
      setSubmitted(true);
      toast.success("Reset link sent!", { description: "Check your email for the reset link" });
    } catch (error) {
      // Always show success to prevent email enumeration
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen tts-navy-bg flex items-center justify-center p-4">
        <div className="w-full max-w-md animate-fade-in">
          <div className="bg-background/95 backdrop-blur-sm rounded-2xl p-8 shadow-2xl border border-white/10 text-center">
            <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-success" />
            </div>
            
            <h1 className="text-2xl font-bold text-foreground mb-2" style={{ fontFamily: "'Sora', sans-serif" }}>
              Check your email
            </h1>
            <p className="text-muted-foreground mb-6">
              If an account exists with <strong>{email}</strong>, we've sent a password reset link.
            </p>

            <p className="text-sm text-muted-foreground mb-6">
              Didn't receive the email? Check your spam folder or try again with a different email.
            </p>

            <Link to="/auth/login">
              <Button className="w-full h-11 btn-primary">
                Back to Sign In
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen tts-navy-bg relative overflow-hidden flex items-center justify-center p-4">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-32 h-32 rounded-full bg-white/5" />
        <div className="absolute bottom-40 right-20 w-48 h-48 rounded-full bg-white/3" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F1923] via-[#1A2B3C] to-[#0F1923]" />
      </div>

      <div className="w-full max-w-md relative z-10 animate-fade-in">
        {/* Back Button */}
        <Link
          to="/auth/login"
          className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-6 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to sign in
        </Link>

        <div className="bg-background/95 backdrop-blur-sm rounded-2xl p-8 shadow-2xl border border-white/10">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <Logo size="lg" />
          </div>

          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-foreground mb-2" style={{ fontFamily: "'Sora', sans-serif" }}>
              Forgot password?
            </h1>
            <p className="text-muted-foreground text-sm">
              Enter your email and we'll send you a reset link
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-11 bg-background/50 border-border/50 focus:border-primary"
                  required
                  data-testid="forgot-email-input"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-11 text-base font-semibold btn-primary"
              disabled={loading}
              data-testid="forgot-submit-btn"
            >
              {loading ? "Sending..." : "Send Reset Link"}
            </Button>
          </form>

          {/* Sign Up Link */}
          <p className="text-center mt-6 text-sm text-muted-foreground">
            Remember your password?{" "}
            <Link to="/auth/login" className="text-primary font-medium hover:text-primary/80 transition-colors">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
